import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import FeatureGate from '../../components/FeatureGate';
import OverviewTab from './tabs/OverviewTab';
import ProductMarginsTab from './tabs/ProductMarginsTab';
import ExpiringStockTab from './tabs/ExpiringStockTab';
import OtherIncomeTab from './tabs/OtherIncomeTab';
import ReceivablesTab from './tabs/ReceivablesTab';
import ReportsTab from './tabs/ReportsTab';
import WastageTab from './tabs/WastageTab';
import TaxSummaryTab from './tabs/TaxSummaryTab';
import BudgetVsActualTab from './tabs/BudgetVsActualTab';
import './Finance.css';

/* Tab registry. Each tab carries its own feature flag so the gate is
   one line, not duplicated copy across the file. Day 5 fills in the
   Margins + Expiring tab bodies. */
const TABS = [
  { id: 'overview',    label: 'Overview',        gate: 'finance.overview' },
  { id: 'margins',     label: 'Product margins', gate: 'finance.product_margins' },
  { id: 'expiring',    label: 'Expiring stock',  gate: 'finance.expiring_stock' },
  { id: 'wastage',     label: 'Wastage',         gate: 'finance.wastage' },
  { id: 'tax',         label: 'Tax summary',     gate: 'finance.tax_summary' },
  { id: 'budget',      label: 'Budget vs actual', gate: 'finance.budgets' },
  { id: 'income',      label: 'Other income',    gate: 'finance.other_income' },
  { id: 'receivables', label: 'Receivables',     gate: 'finance.receivables' },
  { id: 'reports',     label: 'Reports',         gate: 'finance.pnl' },
];

/* Module-scope TabBar (defining components inside the page body causes
   React to unmount+remount on every render, killing input focus — per
   feedback_no_inline_components.md). */
function TabBar({ activeTab, onChange }) {
  return (
    <nav className="fin-tabs" role="tablist" aria-label="Finance sections">
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={activeTab === t.id}
          className={`fin-tab${activeTab === t.id ? ' is-active' : ''}`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </nav>
  );
}

export default function Finance() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = TABS.find((t) => t.id === searchParams.get('tab'))?.id || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabChange = (id) => {
    setActiveTab(id);
    // Preserve other params; just update ?tab=
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  const currentTab = TABS.find((t) => t.id === activeTab) || TABS[0];

  return (
    <div className="fin-page">
      <header className="fin-header">
        <div className="fin-header-text">
          <h1 className="fin-title">Finance</h1>
          <p className="fin-subtitle">
            Track your money in, money out, and what's owed — at a glance.
          </p>
        </div>
      </header>

      <TabBar activeTab={activeTab} onChange={handleTabChange} />

      <section className="fin-tabpanel" role="tabpanel">
        <FeatureGate
          featureId={currentTab.gate}
          upgradeMessage={`Upgrade to unlock ${currentTab.label}`}
        >
          {activeTab === 'overview'    && <OverviewTab />}
          {activeTab === 'margins'     && <ProductMarginsTab />}
          {activeTab === 'expiring'    && <ExpiringStockTab />}
          {activeTab === 'wastage'     && <WastageTab />}
          {activeTab === 'tax'         && <TaxSummaryTab />}
          {activeTab === 'budget'      && <BudgetVsActualTab />}
          {activeTab === 'income'      && <OtherIncomeTab />}
          {activeTab === 'receivables' && <ReceivablesTab />}
          {activeTab === 'reports'     && <ReportsTab />}
        </FeatureGate>
      </section>
    </div>
  );
}
